import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { clientsService } from '../services/api';
import './Map.css';

const GOLD_COAST = [-27.97, 153.38];

// Popup content is built as an HTML string for Leaflet — escape everything
// client-supplied (names/addresses come from the public booking form).
function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}

export default function MapPage() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const [info, setInfo] = useState({ count: 0, pending: 0, loading: true, error: '' });

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current).setView(GOLD_COAST, 11);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);
    mapRef.current = map;

    (async () => {
      const r = await clientsService.mapData();
      if (!r.success) { setInfo({ count: 0, pending: 0, loading: false, error: r.error || 'Failed to load map data' }); return; }
      const pts = [];
      for (const c of r.customers || []) {
        const addr = [c.address_street, c.address_suburb, c.address_state, c.address_postcode].filter(Boolean).join(', ');
        const hasJob = !!c.next_appointment_at;
        // Red/black rather than teal/grey — teal pins vanish against parkland
        // green on the OSM tiles.
        const colour = hasJob ? '#DC2626' : '#171c1d';
        const marker = L.circleMarker([c.lat, c.lng], {
          radius: 9, color: '#ffffff', fillColor: colour, fillOpacity: 0.95, weight: 2
        }).addTo(map);
        const nextJob = hasJob
          ? new Date(c.next_appointment_at).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })
          : null;
        marker.bindPopup([
          `<strong>${esc(c.name)}</strong>`,
          esc(addr),
          c.phone ? `<a href="tel:${esc(c.phone)}">${esc(c.phone)}</a>` : null,
          nextJob ? `Next job: ${esc(nextJob)}` : null,
          `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}" target="_blank" rel="noopener noreferrer">Open in Google Maps ↗</a>`
        ].filter(Boolean).join('<br>'));
        pts.push([c.lat, c.lng]);
      }
      if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 14 });
      setInfo({ count: (r.customers || []).length, pending: r.pendingGeocode || 0, loading: false, error: '' });
    })();

    return () => { map.remove(); mapRef.current = null; };
  }, []);

  return (
    <div className="map-page">
      <div className="page-header">
        <h1 className="page-title">Customer Map</h1>
        <div className="map-legend">
          <span className="legend-item"><span className="legend-dot legend-upcoming"></span> Upcoming job</span>
          <span className="legend-item"><span className="legend-dot legend-other"></span> Past / no booking</span>
        </div>
      </div>

      {info.error && <p className="text-muted">{info.error}</p>}
      {!info.loading && !info.error && info.count === 0 && (
        <p className="text-muted">No customers with mappable addresses yet — pins appear as bookings come in.</p>
      )}
      {info.pending > 0 && (
        <p className="text-muted map-pending">{info.pending} address{info.pending === 1 ? '' : 'es'} still being located — refresh in a minute to see them.</p>
      )}

      <div className="map-container" ref={mapEl}></div>
    </div>
  );
}
